import { useEffect } from 'react';

/** The decision, split out so it can be tested without a DOM.
 *
 *  `fromInputs` is the difference between two kinds of overlay:
 *   - a FORM (create project, rename, stage editor): Esc while typing must NOT
 *     close it, or a stray keypress throws away what someone just typed.
 *   - a SEARCH-like overlay whose whole point IS the text field: Esc has to work
 *     there too, because that is where the cursor always is. */
export function escapeShouldClose(
  key: string,
  target: EventTarget | null,
  fromInputs: boolean,
  isComposing = false,
): boolean {
  if (key !== 'Escape') return false;
  // Mid-IME, Escape cancels the composition — it is not a request to close.
  if (isComposing) return false;
  if (fromInputs) return true;
  const t = target as HTMLElement | null;
  if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return false;
  return true;
}

/** Minimal shape of what this binds to — a fake one makes the WIRING testable,
 *  which is the half that actually broke: the search modal's Escape was attached
 *  to its own `<input>`, so it stopped working the moment focus moved anywhere
 *  else in the modal. */
export interface EscapeTarget {
  addEventListener(type: 'keydown', fn: (e: KeyboardEvent) => void, capture?: boolean): void;
  removeEventListener(type: 'keydown', fn: (e: KeyboardEvent) => void, capture?: boolean): void;
}

/** Bind Escape at the WINDOW — never at an element, or the shortcut only works
 *  while that one element happens to have focus. Returns an unbind. */
export function bindEscape(
  win: EscapeTarget,
  onClose: () => void,
  fromInputs: boolean,
): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (!escapeShouldClose(e.key, e.target, fromInputs, e.isComposing)) return;
    // The topmost overlay claims the key, so nothing underneath (e.g. the task
    // panel) closes on the same press. Capture phase, because a bubble-phase
    // stopPropagation would be too late to stop the other window listeners.
    if (fromInputs) e.stopPropagation();
    onClose();
  };
  win.addEventListener('keydown', onKey, fromInputs);
  return () => win.removeEventListener('keydown', onKey, fromInputs);
}

/** Esc closes the overlay.
 *
 *  By default it does nothing while the user is typing in a field (including
 *  contentEditable, e.g. the description editor).
 *
 *  Pass `{ fromInputs: true }` for a search-style overlay that must close from
 *  anywhere, including its own text box. */
export function useEscapeToClose(onClose: () => void, opts: { fromInputs?: boolean } = {}): void {
  const fromInputs = opts.fromInputs === true;
  useEffect(() => bindEscape(window, onClose, fromInputs), [onClose, fromInputs]);
}
